import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { ProfileType } from '@app/core/http/http-response.model';
import { AddressObject, FEExperience } from '@shared/models/fe-experience.model';
import {
  BEExperience,
  BEExperienceKey,
  ExperienceEndpoint,
  ExperienceTagBE,
  Gallery,
} from '@shared/models/be-experience.model';
import { ExperienceCategory, ExperienceStyle, GoogleMapsService, ILocation, UtilsService } from '@ess-front/shared';
import { ExperienceElementsService } from '@api/experience-elements.service';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { TranslationDomainService } from '@domain/translation-domain.service';
import { EXPERIENCE_TAGS_KEYS } from '@app/utils/constants/tags';
import { StaticTextKey } from '@shared/models/static-text-key.model';

@Injectable({ providedIn: 'root' })
export class ExperienceDetailsService {
  constructor(
    private experienceElementsService: ExperienceElementsService,
    private googleMapsService: GoogleMapsService,
    private translationDomainService: TranslationDomainService,
    private utilsService: UtilsService,
  ) {}

  getFEExperience$(
    isBooking: boolean,
    endPoint: ExperienceEndpoint,
    config: { hash: string | null; id: number | null; slug: string | null },
    reducedVersion = false,
  ): Observable<FEExperience> {
    if (!this.isValidConfig(isBooking, endPoint, config)) {
      const errorMessage = this.translationDomainService.translate(marker('errors.invalidExperienceConfig'));
      throw new Error(errorMessage);
    }
    const language = this.translationDomainService.getActiveLanguage();
    const experience$ = this.selectExperienceObservable$(endPoint, config, language);

    return experience$.pipe(
      switchMap(beExperience => {
        return forkJoin([this.getRelated$(isBooking, beExperience, reducedVersion, language), of(beExperience)]);
      }),
      map(([related, experience]: [ILocation[] | null, BEExperience]) => {
        return this.transformToFEModel(experience, isBooking, endPoint, related);
      }),
    );
  }

  private transformToFEModel(
    beExperience: BEExperience,
    isBooking: boolean,
    endPoint: ExperienceEndpoint,
    related: ILocation[] | null,
  ): FEExperience {
    const address: AddressObject = {
      address: beExperience.address_object?.address ?? beExperience.address ?? null,
      city: beExperience.address_object?.city ?? beExperience.city ?? '',
      country: beExperience.address_object?.country ?? beExperience.country ?? '',
      full_address: beExperience.address_object?.full_address ?? '',
      phone: beExperience.address_object?.phone ?? beExperience.phone ?? null,
      zipcode: beExperience.address_object?.zipcode ?? beExperience.zipcode ?? '',
      neighborhood: beExperience.neighborhood ?? null,
      location: beExperience.location,
      // website is shown for hotels and restaurants
      website: this.getWebsite(beExperience.website, beExperience.category_slug),
      mapsUrl:
        beExperience.location?.latitude && beExperience.location?.longitude
          ? this.googleMapsService.createMapsUrlByCoordinates(
              beExperience.location.latitude,
              beExperience.location.longitude,
            )
          : null,
    };

    const category =
      (isBooking && beExperience.category_slug === ExperienceCategory.HOTEL) ||
      (!isBooking && endPoint === ExperienceEndpoint.Hotel)
        ? beExperience.subcategory
        : beExperience.category;

    return {
      address,
      benefits: beExperience.benefits,
      category: category ?? null,
      categorySlug: beExperience.category_slug ?? null,
      date: beExperience.start_date ?? null,
      description: beExperience.description ?? '',
      editor: beExperience.editor ?? null,
      editorialContent: beExperience.editorial_content ?? [],
      gallery: beExperience.gallery ? this.getGallery(beExperience.gallery) : [],
      goodToKnow: [],
      icon: beExperience.icon ?? null,
      insiders: beExperience.services ?? [],
      isBookingExperience: isBooking,
      quote: beExperience.quote ?? null,
      related: related ?? [],
      statusSlug: beExperience.status_slug ?? null,
      styleSlug: beExperience.style_slug ?? null,
      tags: this.getTags(beExperience, isBooking),
      time: beExperience.from_time_display
        ? this.utilsService.getFormattedTime(
            beExperience.from_time_display,
            this.translationDomainService.getActiveLanguage(),
          )
        : null,
      title: beExperience.title,
      typeSlug: beExperience.type_slug ?? null,
      whatWeLoveGallery: beExperience.what_we_love,
    };
  }

  private getGallery(gallery: Gallery[]): string[] {
    return gallery.map(item => item.local_src);
  }

  private getTags(beExperience: BEExperience, isBooking: boolean): StaticTextKey[] {
    const tags: StaticTextKey[] = Object.keys(beExperience)
      .filter((key): key is ExperienceTagBE => this.isNotEmptyTagKey(key as BEExperienceKey, beExperience))
      .filter(tagKey => tagKey !== 'tag_great_for' || !isBooking)
      .map(tagKey => {
        const titleKey = this.getExperienceTagTitle(tagKey);
        const content = this.getExperienceTagContent(tagKey, beExperience);

        return { titleKey, content };
      });

    return [...tags, ...this.getExtraTags(beExperience)];
  }

  private isNotEmptyTagKey(key: BEExperienceKey, beExperience: BEExperience): boolean {
    const isTagKey = key.indexOf('tag') === 0;
    const isNotEmpty = Array.isArray(beExperience[key]) ? beExperience[key].length > 0 : !!beExperience[key];
    return isTagKey && isNotEmpty;
  }

  private getExtraTags(beExperience: BEExperience): StaticTextKey[] {
    const extraTags: StaticTextKey[] = [];
    if (beExperience.style === ExperienceStyle.SELF_EXPLORE || beExperience.style === ExperienceStyle.INSIDERS) {
      extraTags.push({ titleKey: marker('experience.interest'), content: beExperience.style });
    }
    if (beExperience.subcategory) {
      extraTags.push({ titleKey: marker('experience.experienceType'), content: beExperience.subcategory });
    }
    if (beExperience.duration && beExperience.duration !== '00:00:00') {
      extraTags.push({
        titleKey: marker('experience.duration'),
        content: this.utilsService.getFormattedDuration(beExperience.duration),
      });
    }
    if (beExperience.editor && beExperience.editor.first_name !== 'Essentialist') {
      extraTags.push({
        titleKey: marker('experience.curatedBy'),
        content: `${beExperience.editor.first_name} ${beExperience.editor.last_name}`,
      });
    }
    return extraTags;
  }

  private getExperienceTagTitle(tagKey: ExperienceTagBE): string {
    return EXPERIENCE_TAGS_KEYS[tagKey];
  }

  private getExperienceTagContent(key: BEExperienceKey, beExperience: BEExperience): string {
    if (Array.isArray(beExperience[key])) {
      return beExperience[key].join(', ');
    } else {
      return beExperience[key] as string;
    }
  }

  private selectExperienceObservable$(
    endPoint: string,
    config: { hash: string | null; id: number | null; slug: string | null },
    language: string,
  ): Observable<BEExperience> {
    switch (endPoint) {
      case ProfileType.HOTEL:
        return this.experienceElementsService.getHotel$(config.slug as string, language);
      case ProfileType.EXPERIENCE:
        return this.experienceElementsService.getExperienceBySlug$(config.slug as string, language);
      case ProfileType.BOOKINGEXPERIENCE:
        return this.experienceElementsService.getBookingExperienceByHash$(config.hash as string, config.id as number);
      default:
        return this.experienceElementsService.getPlace$(config.slug as string, language);
    }
  }

  private getRelated$(
    isBooking: boolean,
    beExperience: BEExperience,
    reducedVersion: boolean,
    language: string,
  ): Observable<ILocation[] | null> {
    if (beExperience.style_slug !== ExperienceStyle.SELF_EXPLORE || reducedVersion) {
      return of(null);
    } else {
      const experienceId = isBooking ? beExperience.original_experience_id : beExperience.id;
      return experienceId ? this.experienceElementsService.getRelated$(experienceId, language) : of(null);
    }
  }

  private isValidConfig(
    isBooking: boolean,
    endPoint: ExperienceEndpoint,
    config: { hash: string | null; id: number | null; slug: string | null },
  ) {
    if (isBooking) {
      return !!config.id && !!config.hash && endPoint === ExperienceEndpoint.BookingExperience;
    } else {
      return !!config.slug && endPoint !== ExperienceEndpoint.BookingExperience;
    }
  }

  private getWebsite(website?: string | null, categorySlug?: ExperienceCategory | null): string | null {
    if (!categorySlug || !website) {
      return website ?? null;
    }
    if (categorySlug === ExperienceCategory.HOTEL || categorySlug === ExperienceCategory.FOOD_AND_WINE) {
      return website;
    } else {
      return null;
    }
  }
}
